import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  isClientLoggedIn,
  setClientSessionFromAuthResponse,
} from "utils/clientSession";
import {
  previewClientzoneOtpDestination,
  requestClientzoneOtp,
  verifyClientzoneOtp,
  type ClientzoneOtpPreview,
} from "utils/clientzoneAuthApi";

type LoginStep = "identifier" | "otp";

const GENERIC_OTP_PREVIEW_MESSAGE =
  "If your account exists, a login code will be sent to the primary contact WhatsApp number on file.";

const EMPTY_PREVIEW: ClientzoneOtpPreview = {
  can_request: false,
  message: "",
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function ClientLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>("identifier");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [preview, setPreview] = useState<ClientzoneOtpPreview>(EMPTY_PREVIEW);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const trimmed = identifier.trim();
    if (step !== "identifier" || trimmed.length < 3) {
      setPreview(EMPTY_PREVIEW);
      setIsPreviewLoading(false);
      return undefined;
    }

    let isCancelled = false;
    setIsPreviewLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const nextPreview = await previewClientzoneOtpDestination(trimmed);
        if (!isCancelled) {
          setPreview(nextPreview);
        }
      } catch {
        if (!isCancelled) {
          setPreview({
            can_request: false,
            message: GENERIC_OTP_PREVIEW_MESSAGE,
          });
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    }, 400);

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [identifier, step]);

  if (isClientLoggedIn()) {
    return <Navigate to="/profile" replace />;
  }

  const destinationMessage =
    preview.message ||
    (identifier.trim().length >= 3 && !isPreviewLoading ? GENERIC_OTP_PREVIEW_MESSAGE : "");

  const handleRequestOtp = async () => {
    const trimmed = identifier.trim();
    if (!trimmed) {
      setErrorMessage("Enter your RSA ID number or customer code.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setInfoMessage("");

    if (!preview.can_request) {
      await sleep(600);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await requestClientzoneOtp(trimmed);
      setInfoMessage(response.message);
      setStep("otp");
      setOtp("");
    } catch {
      setInfoMessage(GENERIC_OTP_PREVIEW_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    const trimmedIdentifier = identifier.trim();
    const trimmedOtp = otp.replace(/\D/g, "").slice(0, 6);
    if (!trimmedIdentifier) {
      setErrorMessage("Enter your RSA ID number or customer code.");
      setStep("identifier");
      return;
    }
    if (trimmedOtp.length !== 6) {
      setErrorMessage("Enter the 6-digit code from WhatsApp.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const response = await verifyClientzoneOtp(trimmedIdentifier, trimmedOtp);
      setClientSessionFromAuthResponse(response);
      navigate("/profile", { replace: true });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Invalid or expired login code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CoverLayout>
      <Card sx={{ width: "100%", maxWidth: 420, mx: 2 }}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            NanoTech ClientZone
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Customer Portal
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDTypography variant="body2" color="text" mb={2}>
            Sign in with your RSA ID or customer code. We will send a one-time login code to your
            WhatsApp number on file.
          </MDTypography>

          {step === "identifier" ? (
            <>
              <TextField
                fullWidth
                label="RSA ID or customer code"
                value={identifier}
                onChange={(event) => {
                  setIdentifier(event.target.value);
                  setInfoMessage("");
                  setErrorMessage("");
                }}
                autoComplete="username"
                disabled={isSubmitting}
                sx={{ mb: 1 }}
              />
              {destinationMessage ? (
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  {destinationMessage}
                </MDTypography>
              ) : null}
              {isPreviewLoading ? (
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  Checking account details...
                </MDTypography>
              ) : null}
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleRequestOtp}
                disabled={isSubmitting || isPreviewLoading}
              >
                {isSubmitting ? "Sending code..." : "Send login code"}
              </MDButton>
            </>
          ) : (
            <>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                Signing in as: <strong>{identifier.trim()}</strong>
              </MDTypography>
              <TextField
                fullWidth
                label="6-digit WhatsApp code"
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
                autoComplete="one-time-code"
                disabled={isSubmitting}
                sx={{ mb: 2 }}
              />
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleVerifyOtp}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Verifying..." : "Verify and sign in"}
              </MDButton>
              <MDButton
                variant="text"
                color="info"
                fullWidth
                onClick={() => {
                  setStep("identifier");
                  setOtp("");
                  setErrorMessage("");
                  setInfoMessage("");
                }}
                disabled={isSubmitting}
                sx={{ mt: 1 }}
              >
                Back
              </MDButton>
            </>
          )}

          {infoMessage ? (
            <MDTypography variant="caption" color="success" display="block" mt={2}>
              {infoMessage}
            </MDTypography>
          ) : null}
          {errorMessage ? (
            <MDTypography variant="caption" color="error" display="block" mt={2}>
              {errorMessage}
            </MDTypography>
          ) : null}
        </MDBox>
      </Card>
    </CoverLayout>
  );
}
